<template>
  <router-link :to="{ name: 'trip', params: { tripId: trip.uuid } }" class="trip-card-link">
    <div class="card trip" role="link" tabindex="0" @click="handleBooking" @keydown.enter="handleBooking"
      style="cursor: pointer;">
      <div class="trip-header" :class="{ 'has-thumbnail': trip.thumbnailName }" :style="trip.thumbnailName ? {
        backgroundImage: `url(${config.apiUrl}/uploads/thumbnails/${trip.thumbnailName})`
      } : {}">
        <div class="header-overlay">
          <div class="company-info">
            <img v-if="trip.company.logoName" :src="`${config.apiUrl}/uploads/logos/${trip.company.logoName}`"
              :alt="`Logo ${trip.company.name}`" class="company-logo" />
            <span class="company-name" :class="{ 'can-hidden': trip.company.logoName }">{{ trip.company.name }}</span>
          </div>
          <div class="price">
            <span>à partir de</span>
            <span class="price">{{ localeStore.formatPrice(trip.price) }}</span>
          </div>
        </div>
      </div>
      <div class="trip-footer">
        <div class="trip-times">
          <div class="time-box">
            <span class="time">{{ departureTime }}</span>
            <span class="city">{{ trip.addressToStart.city.name }}</span>
          </div>
          <div class="arrow" aria-hidden="true">→</div>
          <div class="time-box">
            <span class="time">{{ arrivalTime }}</span>
            <span class="city">{{ trip.addressToEnd.city.name }}</span>
          </div>
        </div>
        <div class="trip-info">
          <span class="badge badge-used"><strong>{{ trip.availablePlaces }}</strong> {{ placesLabel }}</span>
          <span v-if="trip.vehicle.airConditioning" class="badge badge-info">❄️ Climatisation</span>
          <span v-if="trip.vehicle.wifi" class="badge badge-info">📶 Wi-Fi</span>
          <span v-if="trip.vehicle.toilet" class="badge badge-pending-payment">🚻 Toilette</span>
          <span v-if="trip.vehicle.tv" class="badge badge-paid">📺 TV</span>
          <span v-if="trip.vehicle.refreshments" class="badge badge-cancelled">🍽️ Restauration</span>
        </div>
        <div class="trip-actions">
          <button class="btn btn-primary" @click.prevent.stop="handleBooking">
            Réserver
          </button>
        </div>
      </div>
    </div>
  </router-link>
</template>

<script setup>
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { config } from '@/config/env.js'
import { useLocaleStore } from '@/stores/localeStore'

const props = defineProps({
  trip: {
    type: Object,
    required: true
  },
  showDate: {
    type: Boolean,
    default: false
  }
})

const router = useRouter()
const localeStore = useLocaleStore()

const departureTime = computed(() => formatTime(props.trip.timeToStart))
const arrivalTime = computed(() => formatTime(props.trip.timeToEnd))

const placesLabel = computed(() =>
  props.trip.availablePlaces > 1 ? 'places restantes' : 'place restante'
)

function formatTime(timeString) {
  if (!timeString) return ''

  return new Date(timeString).toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit'
  })
}

function handleBooking() {
  router.push({
    name: 'trip',
    params: { tripId: props.trip.uuid }
  })
}
</script>

<style scoped>
.card.trip {
  align-items: center;
  flex-flow: row wrap;
  padding: 0;
  gap: 0;
  overflow: hidden;
  border: 2px solid var(--color-border);
  transition: box-shadow 0.3s ease, border-color 0.3s ease, transform 0.3s ease;
  box-shadow: var(--shadow-sm);
}

.card.trip:hover {
  box-shadow: var(--shadow-md);
  border-color: var(--color-feuille);
  transform: scale(1.02);
}

.card.trip:hover .trip-header .company-name {
  opacity: 1;
}

.card.trip:hover .company-logo {
  opacity: 1;
}

.card.trip:hover .header-overlay {
  backdrop-filter: blur(0px);
  -webkit-backdrop-filter: blur(0px);
}

.card .trip-header {
  position: relative;
  width: 100%;
  /* Image de fond limitée à 80% de largeur */
  background-size: 80% auto;
  background-position: left center;
  background-repeat: no-repeat;
  background-color: var(--color-grey-light);
}

/* Overlay avec blur + couleur transparente */
.header-overlay {
  width: 100%;
  height: 100%;
  padding: 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  /* Effet glassmorphism */
  /* background-color: rgba(255, 255, 255, 0.5); */
  background-color: var(--bg-primary-alpha);
  backdrop-filter: blur(3px);
  -webkit-backdrop-filter: blur(3px);
  transition: backdrop-filter 0.3s ease, background-color 0.3s ease;
}

.company-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.company-logo {
  height: 40px;
  width: auto;
  max-width: 120px;
  object-fit: contain;
  background-color: rgba(255, 255, 255, 0.9);
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  opacity: 0.9;
  transition: opacity 0.3s ease;
}

.card .trip-header .company-name {
  font-size: 2.0rem;
  font-weight: 900;
  opacity: 0.6;
  color: var(--color-text-secondary);
  transition: opacity 0.3s ease;
  mix-blend-mode: difference;
  filter: invert(100%);
}

.card .trip-footer {
  border-top: 2px solid var(--bg-black-alpha);
  display: flex;
  padding: 1.5rem;
  width: 100%;
  justify-content: space-between;
  align-items: flex-end;
  gap: 2rem;
}

.card.trip .trip-times {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  flex: 1;
}

.card.trip .time-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  min-width: 80px;
}

.card.trip .time {
  font-size: 1.4rem;
  font-weight: bold;
  color: var(--color-feuille);
}

.card.trip .city {
  font-size: 0.85rem;
  color: var(--color-text-secondary);
  margin-top: 0.25rem;
  text-transform: capitalize;
}

.card.trip .arrow {
  color: var(--color-text-secondary);
  font-size: 1.5rem;
}

.card.trip div.price {
  display: flex;
  flex-flow: column wrap;
  justify-content: flex-end;
  font-size: 0.8rem;
}

.card.trip span.price {
  font-size: 1.8rem;
  font-weight: bold;
  color: var(--color-feuille);
  min-width: 60px;
  text-align: right;
}

.card.trip .price span {
  text-align: right;
}

.trip-info {
  display: flex;
  gap: 0.3rem;
  flex-flow: row wrap;
  font-size: 0.85rem;
  justify-content: flex-end;
  flex: 2;
  width: 100%;
}

@media (max-width: 768px) {
  .card.trip {
    gap: 1.5rem;
  }

  /* Sur mobile, remettre l'image en full width */
  .card .trip-header {
    background-size: cover;
    background-position: center center;
  }

  .company-logo {
    height: 32px;
    max-width: 100px;
  }

  .company-info .company-name {
    font-size: 1.5rem;
  }

  .card .trip-footer {
    border-top: none;
    flex-flow: column wrap;
    align-items: flex-start;
    gap: 1rem;
  }

  .btn-group {
    justify-content: space-between;
    align-items: flex-end !important;
    width: 100%;
    margin-top: 0;
    order: 3;
  }

  .card.trip .trip-times {
    justify-content: space-between;
    gap: 0;
    /* flex: 1 1 100%; */
    width: 100%;
  }

  .card .trip-footer {
    padding-top: 0;
    align-items: center;
    gap: 1.5rem;
  }

  .card .trip-info {
    justify-content: flex-start;
  }

  .card .trip-actions {
    order: 3;
  }

  .card .trip-times {
    order: 2;
  }

  .card .trip-actions,
  .card .trip-info,
  .card .trip-actions button {
    width: 100%;
    flex: 1;
    justify-content: center;
  }

  .card .price span.price {
    text-shadow: var(--shadow-lg);
  }
}
</style>
